import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo, breadcrumbJsonLd } from "../lib/seo";
import { categoryUrl } from "../lib/format";
import { ArticleCard, ArticleGrid } from "../components/articles/ArticleCard";
import { Breadcrumbs } from "../components/common/Breadcrumbs";
import { Pagination } from "../components/common/Pagination";
import { Newsletter } from "../components/common/Newsletter";
import { AdSlot } from "../components/common/AdSlot";
import { PageLoader, EmptyState } from "../components/common/States";
import { Reveal } from "../components/common/Reveal";
import NotFound from "./NotFound";

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [params] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/categories/${categorySlug}`, { params: { page, page_size: 12 } }).then((r) => { setData(r.data); setStatus("ok"); }).catch((e) => setStatus(e?.response?.status === 404 ? "404" : "error"));
  }, [categorySlug, page]);

  const cat = data?.category;
  useSeo({
    title: cat ? `${cat.name}${page > 1 ? ` — Page ${page}` : ""}` : "Category", description: cat?.description, path: categoryUrl(categorySlug),
    robots: page > 1 ? "noindex,follow" : "index,follow",
    jsonLd: cat ? [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: cat.name, path: categoryUrl(cat.slug) }])] : [],
  });

  if (status === "404") return <NotFound />;
  if (status !== "ok") return <PageLoader />;

  const { articles, featured, popular, related_categories } = data;
  const rest = page === 1 && featured ? articles.items.filter((a) => a.id !== featured.id) : articles.items;

  return (
    <>
      <section className="border-b border-ab-border bg-ab-soft">
        <div className="container-ab py-12 lg:py-16">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: cat.name, path: categoryUrl(cat.slug) }]} />
          <h1 className="mt-6 font-serif text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" data-testid="category-heading">{cat.name}</h1>
          <p className="mt-5 max-w-2xl text-lg text-ab-text-2" data-testid="category-description">{cat.description}</p>
          <p className="mt-3 text-sm text-ab-muted">{articles.total} article{articles.total === 1 ? "" : "s"}</p>
        </div>
      </section>

      <div className="container-ab grid gap-14 py-14 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-8">
          {page === 1 && featured && (
            <Reveal className="mb-14"><p className="eyebrow mb-4">Featured</p><ArticleCard article={featured} variant="horizontal" eager /></Reveal>
          )}
          {rest.length ? <ArticleGrid articles={rest} cols={2} /> : <EmptyState title="New articles are on the way" text="This section is being filled with carefully researched pieces. Explore related categories meanwhile." />}
          <Pagination page={articles.page} pages={articles.pages} buildHref={(n) => (n === 1 ? categoryUrl(cat.slug) : `${categoryUrl(cat.slug)}?page=${n}`)} />
        </div>
        <aside className="space-y-10 lg:col-span-4" aria-label="Sidebar">
          {popular.length > 0 && (
            <section className="card-ab p-6" data-testid="category-popular">
              <p className="eyebrow">Popular in {cat.name}</p>
              <ol className="mt-4 space-y-4">
                {popular.map((a, i) => (
                  <li key={a.id} className="flex gap-4">
                    <span className="font-serif text-2xl font-semibold text-ab-border">{String(i + 1).padStart(2, "0")}</span>
                    <ArticleCard article={a} variant="text" />
                  </li>
                ))}
              </ol>
            </section>
          )}
          <AdSlot slot="category-rail" size="rectangle" className="!my-0" />
          {related_categories.length > 0 && (
            <section className="card-ab p-6" data-testid="related-categories">
              <p className="eyebrow">Related Categories</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {related_categories.map((c) => <li key={c.id}><Link to={categoryUrl(c.slug)} className="inline-flex rounded-full border border-ab-border px-4 py-2 text-sm font-medium hover:border-ab-accent hover:bg-ab-accent-soft" data-testid={`related-category-${c.slug}`}>{c.name}</Link></li>)}
              </ul>
            </section>
          )}
          <Newsletter variant="inline" source={`category-${cat.slug}`} />
        </aside>
      </div>
    </>
  );
}
